import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, MessageCircle, Heart, User, Sparkles } from 'lucide-react';
import { messageAPI, premiumAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

export const BottomNav = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [likesCount, setLikesCount] = useState(0);
  
  const loadUnreadCount = useCallback(async () => {
    try {
      const response = await messageAPI.getUnreadCount();
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  }, []);

  const loadLikesCount = useCallback(async () => {
    try {
      const response = await premiumAPI.getWhoLikedMe();
      setLikesCount(response.data.count || 0);
    } catch (error) {
      console.error('Failed to load likes count:', error);
    }
  }, []);
  
  useEffect(() => {
    if (isAuthenticated) {
      loadUnreadCount();
      loadLikesCount();
      
      // Listen for new messages
      const handleWsMessage = (event) => {
        const data = event.detail;
        if (data.type === 'new_message') {
          loadUnreadCount();
        } else if (data.type === 'new_like') {
          loadLikesCount();
        }
      };
      window.addEventListener('ws-message', handleWsMessage);
      
      // Poll every 30 seconds
      const interval = setInterval(() => {
        loadUnreadCount();
        loadLikesCount();
      }, 30000);
      
      return () => {
        window.removeEventListener('ws-message', handleWsMessage);
        clearInterval(interval);
      };
    }
  }, [isAuthenticated, loadUnreadCount, loadLikesCount]);

  const navItems = [
    { to: '/discover', icon: Home, label: 'Discover', badge: 0 },
    { to: '/likes', icon: Sparkles, label: 'Likes', badge: likesCount },
    { to: '/matches', icon: Heart, label: 'Matches', badge: 0 },
    { to: '/messages', icon: MessageCircle, label: 'Messages', badge: unreadCount },
    { to: '/profile', icon: User, label: 'Profile', badge: 0 },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-dark/95 backdrop-blur-xl border-t border-white/10 z-50 safe-bottom" data-testid="bottom-nav">
      <div className="flex justify-around items-center py-3 px-2 max-w-lg mx-auto">
        {navItems.map(({ to, icon: Icon, label, badge }) => {
          const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
          return (
            <NavLink
              key={to}
              to={to}
              className={`relative flex flex-col items-center gap-1 px-3 py-1 rounded-xl transition-colors ${
                isActive ? 'text-gold' : 'text-gray-400 hover:text-white'
              }`}
              data-testid={`nav-${label.toLowerCase()}`}
            >
              <div className="relative">
                <Icon className={`w-6 h-6 ${isActive ? 'fill-gold/20' : ''}`} />
                {badge > 0 && (
                  <div className="absolute -top-2 -right-2 min-w-[18px] h-[18px] bg-gold rounded-full flex items-center justify-center px-1">
                    <span className="text-[10px] font-bold text-black">
                      {badge > 99 ? '99+' : badge}
                    </span>
                  </div>
                )}
              </div>
              <span className="text-xs font-body font-medium">{label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
